// Single source of truth for accepted dial codes. Add a new country here only —
// the rest of the app keys off this list. matchCountryByDialPrefix sorts by
// prefix length descending so longer prefixes always win (e.g. "+1242" beats "+1").

export const COUNTRIES = [
  {
    code: 'SN', dial: '+221', label: 'Senegal', flag: '🇸🇳',
    // ARTP mobile plan: 70/75/76/77/78 + 7 digits. Landline (33) excluded
    // so SMS is never queued to a non-mobile number.
    mobile: /^\+221(70|75|76|77|78)\d{7}$/,
    trunkPrefix: '',
  },
  {
    code: 'JP', dial: '+81', label: 'Japan', flag: '🇯🇵',
    // MIC mobile plan: 70/80/90 + 8 digits. Landlines excluded for the same
    // reason; domestic 0-prefix is stripped when composing E.164.
    mobile: /^\+81(70|80|90)\d{8}$/,
    trunkPrefix: '0',
  },
] as const;

export type CountryCode = typeof COUNTRIES[number]['code'];

export const DEFAULT_COUNTRY: CountryCode = 'SN';

// Returns the country whose dial code is the longest prefix of `value`.
export function matchCountryByDialPrefix(value: string): typeof COUNTRIES[number] | undefined {
  const sorted = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
  return sorted.find((c) => value.startsWith(c.dial));
}

// Returns false when the dial code is not on the allowlist OR the subscriber
// portion doesn't match the country's mobile numbering plan.
export function isValidMobile(e164: string): boolean {
  const country = matchCountryByDialPrefix(e164);
  if (!country) return false;
  return country.mobile.test(e164);
}

// Infer the E.164 form of a plain-digit input (no leading "+"). Returns null
// on any ambiguity — if two countries could match, we reject rather than
// silently routing SMS to the wrong one. Unique match wins.
export function inferPhoneFromDigits(digits: string): string | null {
  if (!digits) return null;
  const matches = new Set<string>();
  for (const c of COUNTRIES) {
    const direct = c.dial + digits;
    if (c.mobile.test(direct)) matches.add(direct);
    if (c.trunkPrefix && digits.startsWith(c.trunkPrefix)) {
      const trimmedDigits = digits.slice(c.trunkPrefix.length);
      const trunked = c.dial + trimmedDigits;
      if (c.mobile.test(trunked)) matches.add(trunked);
    }
  }
  if (matches.size === 1) {
    return [...matches][0];
  }
  return null;
}
